import type {
  Contractor,
  ContractorTransferConflict,
  ContractorTransferPlannedProject,
  ContractorTransferPreview,
  ContractorTransferProjectMember,
  ContractorTransferRequest,
  Gateway,
  Sensor
} from 'src/api'

// Pure state and selection rules of the transfer wizard: no React or MUI imports, so jest can test them directly

/** The admin contractor payload also carries `isActivated`, which the swagger does not document: false = deactivated, null = never activated. */
export type TransferContractor = Contractor & { isActivated?: boolean | null }

export enum TransferWizardStep {
  Contractors = 0,
  Users = 1,
  Projects = 2,
  Devices = 3,
  Summary = 4
}

export const TRANSFER_WIZARD_STEPS = ['Source and target', 'Users', 'Projects', 'Devices', 'Summary']

export interface TransferWizardState {
  step: TransferWizardStep
  source: TransferContractor | null
  target: TransferContractor | null
  userContractorIds: number[]
  extraSensorIds: number[]
  extraGatewayIds: number[]
}

export type TransferWizardAction =
  | { type: 'setSource'; contractor: TransferContractor | null }
  | { type: 'setTarget'; contractor: TransferContractor | null }
  | { type: 'setUsers'; ids: number[] }
  | { type: 'setExtraSensors'; ids: number[] }
  | { type: 'setExtraGateways'; ids: number[] }
  | { type: 'goTo'; step: TransferWizardStep }

export const initialTransferWizardState: TransferWizardState = {
  step: TransferWizardStep.Contractors,
  source: null,
  target: null,
  userContractorIds: [],
  extraSensorIds: [],
  extraGatewayIds: []
}

export const transferWizardReducer = (state: TransferWizardState, action: TransferWizardAction): TransferWizardState => {
  switch (action.type) {
    case 'setSource': {
      if (action.contractor?.id === state.source?.id) return state
      // users and devices belong to the previous source; a target equal to the new source is no longer valid
      return {
        ...state,
        source: action.contractor,
        target: state.target && state.target.id === action.contractor?.id ? null : state.target,
        userContractorIds: [],
        extraSensorIds: [],
        extraGatewayIds: []
      }
    }
    case 'setTarget':
      return { ...state, target: action.contractor }
    case 'setUsers':
      return { ...state, userContractorIds: action.ids }
    case 'setExtraSensors':
      return { ...state, extraSensorIds: action.ids }
    case 'setExtraGateways':
      return { ...state, extraGatewayIds: action.ids }
    case 'goTo':
      return { ...state, step: action.step }
    default:
      return state
  }
}

/** Something the admin would lose on a reload: the browser asks before leaving. */
export const hasWizardSelection = (state: TransferWizardState): boolean => Boolean(state.source || state.target)

export const hasTransferContent = (state: TransferWizardState): boolean => state.userContractorIds.length > 0 || state.extraSensorIds.length > 0 || state.extraGatewayIds.length > 0

export const buildTransferRequest = (state: TransferWizardState, projectIds?: number[]): ContractorTransferRequest | null => {
  if (!state.source || !state.target) return null

  return {
    sourceContractorId: state.source.id,
    targetContractorId: state.target.id,
    userContractorIds: state.userContractorIds,
    ...(projectIds ? { projectIds } : {}),
    extraDevices: { sensorIds: state.extraSensorIds, gatewayIds: state.extraGatewayIds }
  }
}

export const getContractorTypeLabel = (contractor: Pick<Contractor, 'isRoot' | 'rootContractorId'>): string => {
  if (contractor.isRoot) return 'HQ'
  return contractor.rootContractorId ? 'Branch' : 'Standalone'
}

export interface TargetEligibility {
  selectable: boolean
  reason?: string
  warning?: string
}

/** Mirrors the backend: a deactivated target blocks the transfer, a never-activated one or one whose trial ended only warns. */
export const getTargetEligibility = (contractor: TransferContractor, sourceId?: number | null, now: Date = new Date()): TargetEligibility => {
  if (sourceId && contractor.id === sourceId) return { selectable: false, reason: 'Same as the source' }
  if (contractor.isActivated === false) return { selectable: false, reason: 'Deactivated' }
  if (contractor.isActivated !== true) return { selectable: true, warning: 'This contractor is not activated yet.' }

  if (contractor.trialEndedAt && new Date(contractor.trialEndedAt) < now && !contractor.isActivePaidPlan) {
    return { selectable: true, warning: 'The trial of this contractor has ended and it has no paid plan.' }
  }

  return { selectable: true }
}

export interface DeviceProject {
  id: number
  name: string
}

export const getActiveSensorProjects = (sensor: Pick<Sensor, 'projectSectionsSensors'>): DeviceProject[] =>
  (sensor.projectSectionsSensors || [])
    .filter((connection) => connection.isActive && !connection.disconnectTime && connection.projectId)
    .map((connection) => ({ id: connection.projectId as number, name: connection.project?.name || `#${connection.projectId}` }))

export const getActiveGatewayProjects = (gateway: Pick<Gateway, 'gatewayProjects'>): DeviceProject[] =>
  (gateway.gatewayProjects || [])
    .filter((connection) => connection.isActive && !connection.disconnectTime && connection.projectId)
    .map((connection) => ({ id: connection.projectId as number, name: connection.project?.name || `#${connection.projectId}` }))

export type DeviceSelectionKind = 'following' | 'staying' | 'free'

export interface DeviceSelectionState {
  kind: DeviceSelectionKind
  hint?: string
}

const uniqueNames = (projects: DeviceProject[]): string => Array.from(new Set(projects.map((project) => project.name))).join(', ')

/**
 * Mirrors the planner: a device serving only moving projects follows them; one that also serves a staying project
 * stays at the source and cannot be picked; a device serving no project can be added by hand.
 */
export const getDeviceSelectionState = (projects: DeviceProject[], movingProjectIds: number[]): DeviceSelectionState => {
  if (!projects.length) return { kind: 'free' }

  const staying = projects.filter((project) => !movingProjectIds.includes(project.id))
  if (staying.length) return { kind: 'staying', hint: `Serves a staying project: ${uniqueNames(staying)}` }

  return { kind: 'following', hint: `Follows ${uniqueNames(projects)}` }
}

export interface MemberLosingAccess {
  userId: number
  name: string
  email: string
  projects: string[]
}

/** Members who stay at the source and lose the moved projects; owners of the target's HQ keep them. */
export const getMembersLosingAccess = (projects: ContractorTransferPlannedProject[]): MemberLosingAccess[] => {
  const byUser = new Map<number, MemberLosingAccess>()

  for (const project of projects) {
    for (const member of project.members || []) {
      if (member.moves || member.keepsAccess) continue

      const entry = byUser.get(member.userId) ?? { userId: member.userId, name: member.name, email: member.email, projects: [] }
      entry.projects.push(project.name)
      byUser.set(member.userId, entry)
    }
  }

  return Array.from(byUser.values()).sort((left, right) => left.name.localeCompare(right.name))
}

const gatewayIdentifiers = (gateway: Gateway): string[] =>
  [gateway.deviceId, gateway.devEUI, gateway.externalDeviceId, gateway.uniqueId, gateway.name]
    .filter((value): value is string => Boolean(value))
    .map((value) => value.toLowerCase())

/** Gateways are loaded all at once, so a pasted list of identifiers is matched in the browser. */
export const matchGatewaysByIdentifiers = (gateways: Gateway[], identifiers: string[]): { matched: Gateway[]; notFound: string[] } => {
  const matched = new Map<number, Gateway>()
  const notFound: string[] = []

  for (const identifier of identifiers) {
    const needle = identifier.toLowerCase()
    const found = gateways.filter((gateway) => gatewayIdentifiers(gateway).includes(needle))

    if (!found.length) notFound.push(identifier)
    for (const gateway of found) matched.set(gateway.id, gateway)
  }

  return { matched: Array.from(matched.values()), notFound }
}

/**
 * The gateways grid filters in the browser, the way the sensor list searches on the server: one term matches any part
 * of an identifier or the name, several terms (a pasted list) match whole identifiers and report the missing ones.
 */
export const filterGateways = (gateways: Gateway[], identifiers: string[]): { rows: Gateway[]; notFound: string[] } => {
  if (!identifiers.length) return { rows: gateways, notFound: [] }

  if (identifiers.length === 1) {
    const needle = identifiers[0].toLowerCase()
    return { rows: gateways.filter((gateway) => gatewayIdentifiers(gateway).some((value) => value.includes(needle))), notFound: [] }
  }

  const { matched, notFound } = matchGatewaysByIdentifiers(gateways, identifiers)
  return { rows: matched, notFound }
}

export interface StayingMembers {
  losing: ContractorTransferProjectMember[]
  keeping: ContractorTransferProjectMember[]
}

/** The members of a moved project who stay at the source: those who lose it, and the target HQ owners who keep it. */
export const getStayingMembers = (project?: ContractorTransferPlannedProject): StayingMembers => {
  const staying = (project?.members || []).filter((member) => !member.moves)

  return { losing: staying.filter((member) => !member.keepsAccess), keeping: staying.filter((member) => member.keepsAccess) }
}

export const TRANSFER_IN_PROGRESS_CODE = 'TRANSFER_IN_PROGRESS'

export const hasTransferInProgress = (errors: Pick<ContractorTransferConflict, 'code'>[]): boolean => errors.some((error) => error.code === TRANSFER_IN_PROGRESS_CODE)

/** What the summary tells the admin to do next. A running transfer of the same pair is waited out, not fixed. */
export const getSummaryHint = (errors: Pick<ContractorTransferConflict, 'code'>[], nothingToMove: boolean): string => {
  if (nothingToMove) return 'Nothing is selected: pick users or devices first.'
  if (!errors.length) return 'Every rule is fixed: nothing more is asked before the transfer runs.'

  const waiting = hasTransferInProgress(errors)
  const dataErrors = errors.filter((error) => error.code !== TRANSFER_IN_PROGRESS_CODE).length

  if (waiting && !dataErrors) return 'Another transfer between these contractors is still running. Wait until it finishes, then check again.'
  if (waiting) return 'Fix the blocking errors in the data, and wait until the other transfer between these contractors finishes.'

  return 'Fix the blocking errors in the data, then start a new transfer.'
}

/** Decision 14: Run is enabled once the admin has typed the source contractor's name, exactly, ignoring outer spaces. */
export const isSourceNameConfirmed = (typed: string, sourceName?: string | null): boolean => Boolean(sourceName?.trim()) && typed.trim() === sourceName?.trim()

/**
 * The readings rewrite that runs after the move; the preview estimates it, so the row count is approximate, and only a
 * lower bound when some tables were not estimated in time.
 */
export const getReadingsTotals = (preview?: ContractorTransferPreview): { label: string; value: number }[] =>
  preview?.readings
    ? [
        {
          label: preview.readings.notEstimated?.length ? 'Readings to rewrite (at least)' : 'Readings to rewrite (estimate)',
          value: Number(preview.readings.estimatedTotal) || 0
        },
        { label: 'Compressed chunks with them', value: Number(preview.readings.compressedChunks) || 0 },
        { label: 'Data-logger uploads in progress', value: Number(preview.readings.pendingDataLoggerUploads) || 0 }
      ]
    : []
